import {
  Autocomplete,
  Button,
  Notification,
  createTheme,
  MultiSelect,
  Tooltip,
  rem,
} from '@mantine/core';
import notificationClasses from '#src/styles/module/notification.module.scss';
export const theme = createTheme({
  respectReducedMotion: true,
  autoContrast: true,
  luminanceThreshold: 0.3,
  focusRing: 'auto',
  activeClassName: 'active',
  fontFamily: 'Poppins, sans-serif',
  headings: {
    fontFamily: 'Poppins, sans-serif',
    sizes: {
      h1: {
        fontSize: rem(36),
        fontWeight: '400',
        lineHeight: '1.4',
      },
    },
  },
  components: {
    Autocomplete: Autocomplete.extend({
      defaultProps: {
        comboboxProps: {
          arrowPosition: 'side',
          arrowOffset: 6,
          arrowSize: 12,
          zIndex: 'calc(1 + var(--mantine-z-index-overlay))',
          withinPortal: true,
          transitionProps: {
            transition: 'scale-y',
            duration: 500,
          },
        },
      },
    }),
    MultiSelect: MultiSelect.extend({
      defaultProps: {
        comboboxProps: {
          arrowPosition: 'side',
          arrowOffset: 6,
          arrowSize: 12,
          zIndex: 'calc(1 + var(--mantine-z-index-overlay))',
          withinPortal: true,
          transitionProps: {
            transition: 'scale-y',
            duration: 500,
          },
        },
      },
    }),
    Button: Button.extend({
      defaultProps: {
        autoContrast: true,
        variant: 'gradient',
        gradient: {
          from: 'accent.7',
          to: 'accent.3',
          deg: 25,
        },
        size: 'md',
        radius: 'md',
      },
    }),
    Notification: Notification.extend({
      defaultProps: {
        classNames: notificationClasses,
        opacity: '75%',
        radius: 'sm',
        withBorder: true,
        withCloseButton: true,
        closeButtonProps: {
          'aria-label': 'Close notification',
        },
      },
    }),
    Tooltip: Tooltip.extend({
      defaultProps: {
        closeDelay: 250,
        openDelay: 100,
      },
    }),
  },
  defaultGradient: {
    from: 'accent.7',
    to: 'accent.3',
    deg: 25,
  },
  primaryColor: 'secondary',
  primaryShade: {
    light: 3,
    dark: 9,
  },
  colors: {
    'primary': [ // Based on #292a63 ( oklch(0.32 0.1 278.48) )
      'oklch(0.96 0.01 286.2)', // Shade 0
      'oklch(0.9 0.02 286.01)', // Shade 1
      'oklch(0.8 0.04 285.34)', // Shade 2
      'oklch(0.69 0.08 283.31)', // Shade 3
      'oklch(0.59 0.11 282.51)', // Shade 4
      'oklch(0.52 0.13 281.42)', // Shade 5
      'oklch(0.49 0.14 280.41)', // Shade 6
      'oklch(0.44 0.14 279.8)', // Shade 7
      'oklch(0.4 0.13 279.23)', // Shade 8
      'oklch(0.37 0.12 278.26)', // Shade 9 -- Closest to base
    ],
    'secondary': [ // Based on #77378c ( oklch(0.46 0.15 317) )
      'oklch(0.96 0.02 318.96)', // Shade 0
      'oklch(0.92 0.03 318.13)', // Shade 1
      'oklch(0.82 0.07 317.86)', // Shade 2
      'oklch(0.72 0.1 317.51)', // Shade 3
      'oklch(0.64 0.14 317.71)', // Shade 4
      'oklch(0.59 0.16 317.12)', // Shade 5
      'oklch(0.57 0.17 317.31)', // Shade 6
      'oklch(0.51 0.16 317.36)', // Shade 7
      'oklch(0.47 0.15 316.86)', // Shade 8 -- Closest to base
      'oklch(0.42 0.14 316.8)', // Shade 9
    ],
    'alt': [
      'oklch(0.59 0.079 295)', // Shade 0
      'oklch(0.54 0.079 295)', // Shade 1
      'oklch(0.49 0.079 295)', // Shade 2
      'oklch(0.44 0.079 295)', // Shade 3
      'oklch(0.39 0.079 295)', // Shade 4
      'oklch(0.34 0.079 295)', // Shade 5
      'oklch(0.29 0.079 295)', // Shade 6
      'oklch(0.24 0.079 295)', // Shade 7
      'oklch(0.19 0.079 295)', // Shade 8
      'oklch(0.14 0.079 295)', // Shade 9
    ],
    'accent': [ // Based on #fd756e ( oklch(0.72 0.17 25.1) )
      'oklch(0.95 0.02 23.85)', // Shade 0
      'oklch(0.9 0.05 21.09)', // Shade 1
      'oklch(0.8 0.11 23.18)', // Shade 2
      'oklch(0.7 0.22 27.25)', // Shade 3 -- Closest to base
      'oklch(0.65 0.23 28.5)', // Shade 4
      'oklch(0.63 0.24 29.33)', // Shade 5
      'oklch(0.63 0.25 29.43)', // Shade 6
      'oklch(0.55 0.25 27.49)', // Shade 7
      'oklch(0.52 0.22 29.23)', // Shade 8
      'oklch(0.48 0.2 29.23)', // Shade 9
    ],
    'transparent': [
      'oklch(1 0 0 / 0.05)', // Shade 0
      'oklch(1 0 0 / 0.1)', // Shade 1
      'oklch(1 0 0 / 0.2)', // Shade 2
      'oklch(1 0 0 / 0.3)', // Shade 3
      'oklch(1 0 0 / 0.4)', // Shade 4
      'oklch(1 0 0 / 0.5)', // Shade 5
      'oklch(1 0 0 / 0.6)', // Shade 6
      'oklch(1 0 0 / 0.7)', // Shade 7
      'oklch(1 0 0 / 0.8)', // Shade 8
      'oklch(1 0 0 / 0.9)', // Shade 9
    ],
    'transparent-dark': [
      'oklch(0 0 0 / 0.05)', // Shade 0
      'oklch(0 0 0 / 0.1)', // Shade 1
      'oklch(0 0 0 / 0.2)', // Shade 2
      'oklch(0  0 0 / 0.3)', // Shade 3
      'oklch(0 0 0 / 0.4)', // Shade 4
      'oklch(0 0 0 / 0.5)', // Shade 5
      'oklch(0 0 0 / 0.6)', // Shade 6
      'oklch(0 0 0 / 0.7)', // Shade 7
      'oklch(0 0 0 / 0.8)', // Shade 8
      'oklch(0 0 0 / 0.9)', // Shade 9
    ],
  },

  shadows: {
    md: '1px 1px 3px oklch(0 0 0 / 0.25)',
    xl: '5px 5px 3px oklch(0 0 0 / 0.25)',
  },
});

export default theme;
